import {
  RUNNING_SURFACES,
  POOL_LENGTHS,
  SESSION_KINDS,
  RUN_TYPE_LABELS,
  normalizeSessionKind,
  emptySportLog,
  formatPaceLabel,
  derivePaceFromTimeAndDistance,
} from '../../utils/trainingSportSession';
import { formatPrevWeekLabel, previousSportHint } from '../../utils/trainingPrevWeekHint';

/** Formulario atleta — Running / Natación (tiempo digitado). */
export default function SportAthleteLogForm({
  sessionKind,
  prescription = null,
  techniqueUrl = '',
  coachName = '',
  value,
  onChange,
  previousLog = null,
}) {
  const kind = normalizeSessionKind(sessionKind);
  const log = value || emptySportLog(kind);
  const sportPrev = previousSportHint(previousLog);

  const patch = (field, next) => {
    let updated = { ...log, [field]: next };
    if (kind === SESSION_KINDS.RUNNING && ['duration_h', 'duration_m', 'duration_s', 'distance_km'].includes(field)) {
      const derived = derivePaceFromTimeAndDistance(updated);
      if (derived) {
        updated = { ...updated, ...derived };
      }
    }
    onChange?.(updated);
  };

  if (kind === SESSION_KINDS.GYM) return null;

  const who = String(coachName || '').trim() || 'tu entrenador';

  return (
    <div className="rounded-2xl border border-emerald-200 bg-white p-4 sm:p-5 space-y-4 ui-light-surface">
      <div>
        <h3 className="text-base font-bold text-stone-900 m-0">
          {kind === SESSION_KINDS.RUNNING ? 'Hoy toca correr' : 'Hoy toca nadar'}
        </h3>
        <p className="text-sm text-stone-600 mt-1 mb-0">
          {kind === SESSION_KINDS.RUNNING
            ? 'Escribe cuánto duraste y cuántos km hiciste. El ritmo se calcula solo si pones las dos cosas.'
            : 'Escribe cuánto duraste y cuántos metros hiciste. Vas campo por campo, sin prisa.'}
        </p>
        {prescription ? (
          <p className="text-sm text-emerald-900 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 mt-2 mb-0">
            {who === 'tu entrenador' ? 'Tu entrenador programó' : `${who} te programó`}
            {kind === SESSION_KINDS.RUNNING && prescription.run_type
              ? `: ${RUN_TYPE_LABELS[prescription.run_type] || prescription.run_type}`
              : ''}
            {kind === SESSION_KINDS.RUNNING && prescription.distance_target_km
              ? ` · ${prescription.distance_target_km} km`
              : ''}
            {kind === SESSION_KINDS.RUNNING && prescription.pace_target_sec_per_km
              ? ` · ritmo ${formatPaceLabel(prescription.pace_target_sec_per_km)}`
              : ''}
            {prescription.zone_target != null ? ` · zona ${prescription.zone_target}` : ''}
            {prescription.hr_target_ppm != null ? ` · pulso ${prescription.hr_target_ppm}` : ''}
            {kind === SESSION_KINDS.RUNNING && prescription.intervals_note
              ? ` · ${prescription.intervals_note}`
              : ''}
            {kind === SESSION_KINDS.SWIMMING && prescription.pool_length_m
              ? ` · piscina ${prescription.pool_length_m} m`
              : ''}
            . Tú solo registra lo que salió hoy.
          </p>
        ) : null}
        {sportPrev ? (
          <p className="text-xs text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5 mt-2 mb-0 leading-snug">
            {formatPrevWeekLabel(sportPrev.fecha)}: {sportPrev.lines.join(' · ')}
          </p>
        ) : null}
        {String(techniqueUrl || '').trim() ? (
          <a
            href={techniqueUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex text-center text-sm font-semibold rounded-xl bg-black text-white px-3 py-2 hover:bg-stone-800 transition-all"
          >
            Ver video de técnica
          </a>
        ) : null}
      </div>

      <div>
        <label className="text-sm font-bold text-stone-800">¿Cuánto duraste?</label>
        <div className="flex flex-wrap items-end gap-2 mt-1">
          {[
            { key: 'duration_h', label: 'Horas', max: 23 },
            { key: 'duration_m', label: 'Minutos', max: 59 },
            { key: 'duration_s', label: 'Segundos', max: 59 },
          ].map((f) => (
            <div key={f.key} className="w-[5.5rem]">
              <span className="text-xs text-stone-500">{f.label}</span>
              <input
                type="number"
                min={0}
                max={f.max}
                inputMode="numeric"
                className="input w-full text-base h-11"
                value={log[f.key] ?? ''}
                onChange={(e) => patch(f.key, e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-sm font-bold text-stone-800">Pulso medio (si lo mediste)</label>
          <input
            type="number"
            inputMode="numeric"
            className="input w-full text-base h-11 mt-1"
            placeholder="Opcional"
            value={log.hr_avg_ppm ?? ''}
            onChange={(e) => patch('hr_avg_ppm', e.target.value)}
          />
        </div>
        <div>
          <label className="text-sm font-bold text-stone-800">Zona (1 suave — 5 fuerte)</label>
          <div className="flex gap-1 mt-1">
            {[1, 2, 3, 4, 5].map((z) => (
              <button
                key={z}
                type="button"
                className={Number(log.zone) === z ? 'btn-primary text-sm px-3' : 'btn-secondary text-sm px-3'}
                onClick={() => patch('zone', String(z))}
              >
                {z}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-sm font-bold text-stone-800">Calorías</label>
          <input
            type="number"
            inputMode="numeric"
            className="input w-full text-base h-11 mt-1"
            value={log.calories ?? ''}
            placeholder="Si las tienes, si no déjalo vacío"
            onChange={(e) => patch('calories', e.target.value)}
          />
        </div>
      </div>

      {kind === SESSION_KINDS.RUNNING ? (
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-stone-700">Superficie / pista</label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {RUNNING_SURFACES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={log.surface === s.id ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
                  onClick={() => patch('surface', s.id)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-bold text-stone-800">Kilómetros que corriste</label>
              <input
                type="number"
                step="0.1"
                inputMode="decimal"
                className="input w-full text-base h-11 mt-1"
                placeholder={prescription?.distance_target_km ? `Objetivo ${prescription.distance_target_km}` : 'Ej. 8.5'}
                value={log.distance_km ?? ''}
                onChange={(e) => patch('distance_km', e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-bold text-stone-800">Ritmo (min:seg por km)</label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="number"
                  min={0}
                  inputMode="numeric"
                  className="input w-full text-base h-11"
                  value={log.pace_min ?? ''}
                  placeholder="min"
                  onChange={(e) => patch('pace_min', e.target.value)}
                />
                <span className="text-stone-500">:</span>
                <input
                  type="number"
                  min={0}
                  max={59}
                  inputMode="numeric"
                  className="input w-full text-base h-11"
                  value={log.pace_sec ?? ''}
                  placeholder="seg"
                  onChange={(e) => patch('pace_sec', e.target.value)}
                />
              </div>
            </div>
            {prescription?.include_brick ? (
            <div>
              <label className="text-xs font-bold text-stone-700">Transición bici→run (seg)</label>
              <input
                type="number"
                className="input w-full text-sm h-9 mt-1"
                value={log.transition_bike_run_pace_sec ?? ''}
                placeholder="Si aplica"
                onChange={(e) => patch('transition_bike_run_pace_sec', e.target.value)}
              />
            </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {kind === SESSION_KINDS.SWIMMING ? (
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-stone-700">Piscina</label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {POOL_LENGTHS.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={Number(log.pool_length_m) === m ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
                  onClick={() => patch('pool_length_m', m)}
                >
                  {m} m
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-bold text-stone-800">Metros que nadaste</label>
              <input
                type="number"
                inputMode="numeric"
                className="input w-full text-base h-11 mt-1"
                value={log.distance_m ?? ''}
                onChange={(e) => patch('distance_m', e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-bold text-stone-800">Ritmo /100 m (seg)</label>
              <input
                type="number"
                inputMode="numeric"
                className="input w-full text-base h-11 mt-1"
                value={log.pace_per_100_sec ?? ''}
                onChange={(e) => patch('pace_per_100_sec', e.target.value)}
              />
            </div>
          </div>
        </div>
      ) : null}

      <div>
        <label className="text-sm font-bold text-stone-800">Cuéntale a tu entrenador cómo te fue</label>
        <textarea
          className="input w-full text-base mt-1 min-h-[88px]"
          value={log.notes ?? ''}
          onChange={(e) => patch('notes', e.target.value)}
          placeholder="Cómo te sentiste, clima, si aflojaste o apuraste…"
        />
      </div>
    </div>
  );
}
